import { atom } from "jotai";
import { RESET } from "jotai/utils";
import { authTokenKey, redirectUrl, refreshTokenKey } from "@stringf/services/auth";
import { revokeSession } from "@stringf/services/api";
import { storedDeviceId } from "@stringf/services/device";
import { getItem, setItem } from "@stringf/services/storage";
import { authStateAtom } from "./auth";
import { accountsAtom, clearPendingAccountRequests } from "./accounts";
import { loopedInIdsAtom } from "./loops";
import { clearAllSocket } from "./socket";

/**
 * Why the session is ending. `logout` is the user asking; `expired` is the server
 * having refused the session (a refused refresh, no token, a token that does not
 * decode), so the user is sent back to where they were once they sign in again.
 */
export type SessionEnd = "logout" | "expired";

/**
 * The parts of a sign-out that belong to the app rather than to the store: the
 * query cache, the socket, push, the router. Registered by whoever owns them;
 * every registration is called, and the last `navigate`/`currentPath` wins.
 */
export interface SessionHooks {
  clearQueries?: () => void;
  closeSocket?: () => void;
  unregisterPush?: () => unknown;
  navigate?: (to: string) => void;
  /** Path and search of the current location. */
  currentPath?: () => string;
}

const registered: SessionHooks[] = [];

/** Adds `hooks` to every sign-out until the returned function is called. */
export function registerSessionHooks(hooks: SessionHooks): () => void {
  registered.push(hooks);
  return () => {
    const i = registered.indexOf(hooks);
    if (i >= 0) registered.splice(i, 1);
  };
}

const each = (name: "clearQueries" | "closeSocket" | "unregisterPush") => {
  for (const hooks of [...registered]) {
    try {
      void hooks[name]?.();
    } catch (error) {
      // One part failing to let go must not keep the rest signed in.
      console.warn(`endSession: ${name} failed`, error);
    }
  }
};

const latest = <K extends "navigate" | "currentPath">(name: K): SessionHooks[K] =>
  [...registered].reverse().find((hooks) => hooks[name])?.[name];

/**
 * Device preferences outlive a session; everything else in localStorage is the
 * account's. An allow-list, so a key added later is wiped by default: tokens,
 * `auth`, `accounts`, `loopedIn`, `lastMessageID`, the per-room `unseen:*`, and
 * `ownAnonUserId:*`, which on a shared device would tie a Loop pseudonym to
 * whoever signs in next.
 */
export const outlivesSession = (key: string) => key.startsWith("astring-") || key === "i18nextLng";

const wipeSessionStorage = () => {
  if (typeof localStorage === "undefined") return;
  const keys: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key !== null && !outlivesSession(key)) keys.push(key);
  }
  for (const key of keys) localStorage.removeItem(key);
};

/**
 * The signed-out pages. A sign-out does not navigate away from one (the user is
 * already where they need to be, perhaps mid sign-up), and never returns to one.
 */
const AUTH_PAGES = ["/login", "/signup", "/password-reset", "/forgot-passkey"];
const onAuthPage = (path: string) => {
  const pathname = path.split(/[?#]/)[0];
  return AUTH_PAGES.some((page) => pathname === page || pathname.startsWith(`${page}/`));
};

const inApp = (path: string) => /^\/y(\/|$|[?#])/.test(path);

let ending = false;

/**
 * Ends the session: everything user-scoped goes, and the user lands on `/login`.
 *
 * The one routine for both a manual logout and a forced one, and idempotent: a
 * refused refresh, the socket and a 401 can all report the same dead session,
 * and only the first finds anything to end. The local wipe comes first and never
 * waits on the network; for a manual logout the server is told afterwards,
 * best-effort.
 */
export const endSessionAtom = atom(null, (get, set, reason: SessionEnd) => {
  const auth = get(authStateAtom);
  const token = getItem(authTokenKey);
  const refresh = getItem(refreshTokenKey);
  // Read before the wipe below forgets it: the server is told which Device left.
  const deviceId = storedDeviceId(auth.user?.id);
  if (ending || !(token || refresh || auth.user || auth.anoUser)) return;
  ending = true;
  try {
    const here = latest("currentPath")?.();

    each("closeSocket");
    set(clearAllSocket);
    set(authStateAtom, RESET);
    set(accountsAtom, RESET);
    set(loopedInIdsAtom, RESET);
    clearPendingAccountRequests();
    wipeSessionStorage();
    each("clearQueries");
    each("unregisterPush");

    // Only the signed-in app sends you to `/login`; a dead session found on the
    // landing or another public page is wiped quietly and the page stays.
    if (!here || inApp(here) || (reason === "logout" && !onAuthPage(here))) {
      if (reason === "expired" && here && inApp(here)) setItem(redirectUrl, here);
      latest("navigate")?.("/login");
    }

    if (reason === "logout" && refresh) {
      void revokeSession({ token, refresh, accountId: auth.user?.id, deviceId });
    }
  } finally {
    ending = false;
  }
});
